// ./application-steps/StepContent6.js

import React, { memo } from "react";
import { useGetActiveScholarshipsQuery } from "../../../services/gen-app";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import FormRow from "../../helpers/FormRow";
import FormInput from "../../helpers/FormInput";
import FormSelect from "../../helpers/FormSelect";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";

function StepContent6({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
  appID,
}) {
  const { data: activeScholarshipsData } = useGetActiveScholarshipsQuery(
    appID,
    {
      skip: !appID,
    }
  );

  const MAX_PERSONAL_INFO_WORDS = 750;

  const getWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  const personalInfoWordCount = getWordCount(formData.personal_info || "");
  if (
    process.env.NODE_ENV === "production" &&
    window.location.hash === "#test-error"
  ) {
    throw new Error("Test error boundary!");
  }

  const handlePersonalInfoChange = (e) => {
    const raw = e?.target?.value ?? "";
    const words = raw.trim().split(/\s+/);

    // If word count exceeds limit, truncate to max words
    if (words.length > MAX_PERSONAL_INFO_WORDS && raw.trim()) {
      const trimmed = words.slice(0, MAX_PERSONAL_INFO_WORDS).join(" ");
      handleChange("personal_info")({
        target: { ...e.target, value: trimmed, type: "text" },
      });
    } else {
      handleChange("personal_info")({
        target: { ...e.target, value: raw, type: "text" },
      });
    }
  };

  const activeScholarships = activeScholarshipsData?.data || [];

  const getScholarshipId = (value) => {
    if (!value) return null;
    const idString = value.split(".")[0];
    const id = parseInt(idString, 10);
    return isNaN(id) ? null : id;
  };

  const choices = ["choice1", "choice2", "choice3", "choice4", "choice5"];

  const getStatus = (field, isRequired) => {
    if (stepErrors[field]) return "error";
    if (isRequired && formData[field]) return "success";
    if (!isRequired && !formData[field]) return "warning";
    return "";
  };

  // ✅ Gate Award Considerations by school setting from Step 1
  const showAwardConsiderations = Boolean(formData?.showAwardConsiderations);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
      className="recommendations-form"
    >
      <form className="form-group">
        {/* Header */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={800} textAlign="center">
            Background and Goals
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mx: "auto" }}
            textAlign="left"
          >
            Please share anything you want the selection committee to understand
            about your background, circumstances, and goals.
          </Typography>
        </Stack>

        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> This section can greatly strengthen your
            application. Include special circumstances, personal challenges,
            career plans, qualifications, or anything that helps explain your
            story.
          </Typography>
        </Alert>

        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Please list <strong>in point form</strong> any important information
          you would like the selection committee to know. Emphasize details that
          support your application.
        </Typography>

        <FormRow spacing={2}>
          <Grid item xs={12}>
            <FormInput
              label="Personal Information"
              id="personal_info"
              value={formData.personal_info}
              onChange={handlePersonalInfoChange}
              multiline
              rows={6}
              disabled={isReadOnly}
              status={getStatus("personal_info", false)}
              helperText={stepErrors.personal_info || ""}
            />

            <Box sx={{ mt: 0.75, display: "flex", justifyContent: "flex-end" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color:
                    personalInfoWordCount >= MAX_PERSONAL_INFO_WORDS
                      ? "error.main"
                      : "text.secondary",
                }}
              >
                {personalInfoWordCount} / {MAX_PERSONAL_INFO_WORDS} words
              </Typography>
            </Box>

            {personalInfoWordCount >= MAX_PERSONAL_INFO_WORDS && (
              <Typography
                variant="caption"
                sx={{
                  color: "error.main",
                  display: "block",
                  textAlign: "right",
                  mt: 0.25,
                  fontWeight: 600,
                }}
              >
                Max {MAX_PERSONAL_INFO_WORDS} words reached.
              </Typography>
            )}
          </Grid>
        </FormRow>

        {/* ✅ Only show if school allows it */}
        {showAwardConsiderations && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Header */}
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h5" fontWeight={800} textAlign="center">
                Award Considerations
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mx: "auto" }}
                textAlign="left"
              >
                Select up to five awards you’d like to be considered for.
              </Typography>
            </Stack>

            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2">
                By completing this General Application, you will be considered
                for <strong>all</strong> awards selected by School and by
                Committee. Use the choices below to highlight your top five, and
                briefly explain your reasons above in{" "}
                <strong>Personal Information</strong>.
              </Typography>
            </Alert>

            <FormRow spacing={2}>
              {choices.map((choice, index) => (
                <Grid item xs={12} key={choice}>
                  <FormSelect
                    label={`Choice ${index + 1}`}
                    id={choice}
                    value={(() => {
                      const validValues = activeScholarships.map(
                        (s) => `${s.id}. ${s.name}`
                      );
                      return validValues.includes(formData[choice])
                        ? formData[choice]
                        : "";
                    })()}
                    onChange={handleChange(choice)}
                    disabled={isReadOnly}
                    error={Boolean(stepErrors[choice])}
                    helperText={stepErrors[choice] || ""}
                    status={getStatus(choice, false)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>

                    {activeScholarships.map((scholarship) => {
                      const isSelected = choices
                        .filter((c) => c !== choice)
                        .some(
                          (c) =>
                            getScholarshipId(formData[c]) === scholarship?.id
                        );

                      return (
                        <MenuItem
                          key={scholarship.id}
                          value={`${scholarship.id}. ${scholarship.name}`}
                          disabled={isSelected}
                        >
                          {scholarship.name}
                        </MenuItem>
                      );
                    })}
                  </FormSelect>
                </Grid>
              ))}
            </FormRow>

            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mt: 1 }}
            >
              Note: Options you already selected in another choice are disabled
              to prevent duplicates.
            </Typography>
          </>
        )}
      </form>
    </Paper>
  );
}

export default memo(StepContent6);
